import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Heart, Plus, Search, Users, Calendar, MapPin, Clock, CheckCircle, XCircle,
  Eye, RefreshCw, Loader2, ChevronRight, User, Mail, Phone, Briefcase, Star
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  'Aberta': { color: 'bg-green-500', label: 'Aberta' },
  'Em_Curso': { color: 'bg-blue-500', label: 'Em Curso' },
  'Concluida': { color: 'bg-slate-500', label: 'Concluída' },
  'Cancelada': { color: 'bg-red-500', label: 'Cancelada' }
};

const TIPOS_MISSAO = [
  { id: 'Presencial', label: 'Presencial', icon: MapPin },
  { id: 'Remoto', label: 'Remoto', icon: Briefcase },
  { id: 'Hibrido', label: 'Híbrido', icon: Users }
];

const COMPETENCIAS = [
  'Acompanhamento de Idosos',
  'Apoio Administrativo',
  'Apoio Escolar',
  'Artes e Cultura',
  'Cuidados Infantis',
  'Desporto',
  'Distribuição de Alimentos',
  'Formação e Ensino',
  'Gestão de Eventos',
  'Informática',
  'Línguas Estrangeiras',
  'Manutenção e Bricolage',
  'Marketing e Comunicação',
  'Saúde e Primeiros Socorros',
  'Transporte',
  'Outro'
];

const CANDIDATURA_STATUS = {
  'Pendente': { color: 'bg-amber-500', label: 'Pendente' },
  'Confirmado': { color: 'bg-green-500', label: 'Confirmado' },
  'Rejeitado': { color: 'bg-red-500', label: 'Rejeitado' },
  'Cancelada_Instituicao': { color: 'bg-slate-500', label: 'Cancelada' }
};

export default function PortalVoluntariadoPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [missoes, setMissoes] = useState([]);
  const [filtroStatus, setFiltroStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  const [novoModalOpen, setNovoModalOpen] = useState(false);
  const [detalheModalOpen, setDetalheModalOpen] = useState(false);
  const [selectedMissao, setSelectedMissao] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    titulo: '',
    descricao: '',
    tipo_missao: 'Presencial',
    competencias_necessarias: [],
    numero_voluntarios: 1,
    data_inicio: '',
    data_fim: '',
    horas_estimadas: '',
    horario: '',
    localizacao: '',
    requisitos_especiais: ''
  });

  useEffect(() => {
    loadMissoes();
  }, []);

  const loadMissoes = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/missoes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMissoes(data);
      } else if (response.status === 401) {
        navigate('/portal/login');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao carregar missões');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitMissao = async () => {
    if (!formData.titulo || !formData.descricao || !formData.data_inicio) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('portal_token');
      // Prepare payload with proper types (horas_estimadas must be int)
      const payload = {
        ...formData,
        horas_estimadas: formData.horas_estimadas ? parseInt(formData.horas_estimadas) : 0,
        numero_voluntarios: formData.numero_voluntarios ? parseInt(formData.numero_voluntarios) : 1
      };
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/missoes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Missão ${data.codigo} criada com sucesso!`);
        setNovoModalOpen(false);
        resetForm();
        loadMissoes();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erro ao criar missão');
      }
    } catch (error) {
      toast.error('Erro ao criar missão');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      titulo: '',
      descricao: '',
      tipo_missao: 'Presencial',
      competencias_necessarias: [],
      numero_voluntarios: 1,
      data_inicio: '',
      data_fim: '',
      horas_estimadas: '',
      horario: '',
      localizacao: '',
      requisitos_especiais: ''
    });
  };

  const openDetail = async (missao) => {
    try {
      const token = localStorage.getItem('portal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/missoes/${missao.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedMissao(data);
        setDetalheModalOpen(true);
      }
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const handleAceitarCandidatura = async (candidaturaId) => {
    try {
      const token = localStorage.getItem('portal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/candidaturas/${candidaturaId}/aceitar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Voluntário aceite!');
        openDetail(selectedMissao);
        loadMissoes();
      }
    } catch (error) {
      toast.error('Erro ao aceitar candidatura');
    }
  };

  const handleRejeitarCandidatura = async (candidaturaId) => {
    try {
      const token = localStorage.getItem('portal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/candidaturas/${candidaturaId}/rejeitar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Candidatura rejeitada');
        openDetail(selectedMissao);
      }
    } catch (error) {
      toast.error('Erro ao rejeitar candidatura');
    }
  };

  const handleCancelarMissao = async (missaoId) => {
    if (!confirm('Tem a certeza que deseja cancelar esta missão?')) return;
    
    try {
      const token = localStorage.getItem('portal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/missoes/${missaoId}/cancelar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Missão cancelada');
        setDetalheModalOpen(false);
        loadMissoes();
      }
    } catch (error) {
      toast.error('Erro ao cancelar missão');
    }
  };

  const handleConcluirMissao = async (missaoId) => {
    try {
      const token = localStorage.getItem('portal_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/missoes/${missaoId}/concluir`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Missão concluída!');
        setDetalheModalOpen(false);
        loadMissoes();
      }
    } catch (error) {
      toast.error('Erro ao concluir missão');
    }
  };

  const toggleCompetencia = (comp) => {
    setFormData(prev => ({
      ...prev,
      competencias_necessarias: prev.competencias_necessarias.includes(comp)
        ? prev.competencias_necessarias.filter(c => c !== comp)
        : [...prev.competencias_necessarias, comp]
    }));
  };

  const filteredMissoes = missoes.filter(m => {
    if (filtroStatus !== 'all' && m.status !== filtroStatus) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return m.titulo?.toLowerCase().includes(search) ||
             m.codigo?.toLowerCase().includes(search);
    }
    return true;
  });

  const getStatusCounts = () => {
    const counts = { total: missoes.length };
    Object.keys(STATUS_CONFIG).forEach(status => {
      counts[status] = missoes.filter(m => m.status === status).length;
    });
    return counts;
  };

  const counts = getStatusCounts();
  const totalCandidaturas = missoes.reduce((acc, m) => acc + (m.total_candidaturas || 0), 0);
  const totalConfirmados = missoes.reduce((acc, m) => acc + (m.voluntarios_confirmados || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="voluntariado-loading">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="portal-voluntariado-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Heart className="w-6 h-6 text-pink-600" />
            Voluntariado
          </h1>
          <p className="text-slate-500">Criar missões e gerir candidaturas de voluntários</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadMissoes}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
          <Button onClick={() => setNovoModalOpen(true)} className="bg-pink-600 hover:bg-pink-700">
            <Plus className="w-4 h-4 mr-2" />
            Nova Missão
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-pink-50 to-white">
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-pink-600">{counts.total}</p>
            <p className="text-sm text-slate-500">Total Missões</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-green-600">{counts.Aberta || 0}</p>
            <p className="text-sm text-slate-500">Abertas</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-blue-600">{counts.Em_Curso || 0}</p>
            <p className="text-sm text-slate-500">Em Curso</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-amber-600">{totalCandidaturas}</p>
            <p className="text-sm text-slate-500">Candidaturas</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-2xl font-bold text-purple-600">{totalConfirmados}</p>
            <p className="text-sm text-slate-500">Confirmados</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Pesquisar por título ou código..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filtroStatus} onValueChange={setFiltroStatus}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Status</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Missoes List */}
      <Card>
        <CardContent className="pt-6">
          {filteredMissoes.length === 0 ? (
            <div className="text-center py-12">
              <Heart className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500 mb-4">
                {missoes.length === 0 ? 'Ainda não criou missões de voluntariado' : 'Nenhuma missão encontrada'}
              </p>
              <Button onClick={() => setNovoModalOpen(true)} className="bg-pink-600 hover:bg-pink-700">
                <Plus className="w-4 h-4 mr-2" />
                Criar Primeira Missão
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Código</TableHead>
                  <TableHead>Título</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Data Início</TableHead>
                  <TableHead>Candidaturas</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMissoes.map((missao) => {
                  const statusConfig = STATUS_CONFIG[missao.status] || STATUS_CONFIG['Aberta'];
                  const tipoInfo = TIPOS_MISSAO.find(t => t.id === missao.tipo_missao);
                  const TipoIcon = tipoInfo?.icon || Users;
                  
                  return (
                    <TableRow key={missao.id} className="cursor-pointer hover:bg-slate-50" onClick={() => openDetail(missao)}>
                      <TableCell className="font-mono font-medium">{missao.codigo}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{missao.titulo}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <TipoIcon className="w-4 h-4 text-slate-400" />
                          <span>{tipoInfo?.label || missao.tipo_missao}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {new Date(missao.data_inicio).toLocaleDateString('pt-PT')}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-slate-400" />
                          <span className="text-sm">
                            {missao.voluntarios_confirmados || 0}/{missao.numero_voluntarios}
                          </span>
                          {(missao.total_candidaturas || 0) > 0 && (
                            <Badge variant="outline" className="text-xs">
                              {missao.total_candidaturas} candidatos
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statusConfig.color} text-white`}>
                          {statusConfig.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openDetail(missao); }}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* New Mission Modal */}
      <Dialog open={novoModalOpen} onOpenChange={setNovoModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nova Missão de Voluntariado</DialogTitle>
            <DialogDescription>
              Crie uma missão para receber candidaturas de voluntários
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Título da Missão *</Label>
              <Input
                value={formData.titulo}
                onChange={(e) => setFormData(prev => ({ ...prev, titulo: e.target.value }))}
                placeholder="Ex: Apoio na distribuição semanal de alimentos"
              />
            </div>

            <div className="space-y-2">
              <Label>Descrição *</Label>
              <Textarea
                value={formData.descricao}
                onChange={(e) => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                placeholder="Descreva as atividades, responsabilidades e o que espera dos voluntários..."
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo de Missão</Label>
                <Select value={formData.tipo_missao} onValueChange={(v) => setFormData(prev => ({ ...prev, tipo_missao: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOS_MISSAO.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Nº Voluntários Necessários *</Label>
                <Input
                  type="number"
                  min={1}
                  value={formData.numero_voluntarios}
                  onChange={(e) => setFormData(prev => ({ ...prev, numero_voluntarios: parseInt(e.target.value) || 1 }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data Início *</Label>
                <Input type="date" value={formData.data_inicio} onChange={(e) => setFormData(prev => ({ ...prev, data_inicio: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Data Fim</Label>
                <Input type="date" value={formData.data_fim} onChange={(e) => setFormData(prev => ({ ...prev, data_fim: e.target.value }))} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Horas Estimadas</Label>
                <Input
                  type="number"
                  value={formData.horas_estimadas}
                  onChange={(e) => setFormData(prev => ({ ...prev, horas_estimadas: e.target.value }))}
                  placeholder="Ex: 4"
                />
              </div>
              <div className="space-y-2">
                <Label>Horário</Label>
                <Input
                  value={formData.horario}
                  onChange={(e) => setFormData(prev => ({ ...prev, horario: e.target.value }))}
                  placeholder="Ex: Sábados 9h-13h"
                />
              </div>
            </div>

            {formData.tipo_missao !== 'Remoto' && (
              <div className="space-y-2">
                <Label>Localização</Label>
                <Input
                  value={formData.localizacao}
                  onChange={(e) => setFormData(prev => ({ ...prev, localizacao: e.target.value }))}
                  placeholder="Morada ou local de encontro"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Competências Necessárias</Label>
              <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg max-h-[150px] overflow-y-auto">
                {COMPETENCIAS.map(comp => (
                  <button
                    key={comp}
                    type="button"
                    onClick={() => toggleCompetencia(comp)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      formData.competencias_necessarias.includes(comp)
                        ? 'bg-pink-500 text-white'
                        : 'bg-white border border-slate-200 text-slate-700 hover:border-pink-300'
                    }`}
                  >
                    {comp}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Requisitos Especiais</Label>
              <Textarea
                value={formData.requisitos_especiais}
                onChange={(e) => setFormData(prev => ({ ...prev, requisitos_especiais: e.target.value }))}
                placeholder="Carta de condução, certificado de formação, etc."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setNovoModalOpen(false); resetForm(); }}>
              Cancelar
            </Button>
            <Button onClick={handleSubmitMissao} disabled={submitting} className="bg-pink-600 hover:bg-pink-700">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Heart className="w-4 h-4 mr-2" />}
              Criar Missão
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={detalheModalOpen} onOpenChange={setDetalheModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Missão {selectedMissao?.codigo}</span>
              {selectedMissao && (
                <Badge className={`${STATUS_CONFIG[selectedMissao.status]?.color || 'bg-slate-500'} text-white`}>
                  {STATUS_CONFIG[selectedMissao.status]?.label || selectedMissao.status}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedMissao && (
            <Tabs defaultValue="detalhes" className="mt-4">
              <TabsList>
                <TabsTrigger value="detalhes">Detalhes</TabsTrigger>
                <TabsTrigger value="candidaturas">
                  Candidaturas ({selectedMissao.candidaturas?.length || 0})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="detalhes" className="space-y-4">
                <div>
                  <h3 className="font-semibold text-lg">{selectedMissao.titulo}</h3>
                  <p className="text-slate-600 mt-2 whitespace-pre-wrap">{selectedMissao.descricao}</p>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <span>
                      {new Date(selectedMissao.data_inicio).toLocaleDateString('pt-PT')}
                      {selectedMissao.data_fim && ` - ${new Date(selectedMissao.data_fim).toLocaleDateString('pt-PT')}`}
                    </span>
                  </div>
                  {selectedMissao.horario && (
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-slate-400" />
                      <span>{selectedMissao.horario}</span>
                    </div>
                  )}
                  {selectedMissao.localizacao && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <span>{selectedMissao.localizacao}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-slate-400" />
                    <span>{selectedMissao.candidaturas?.filter(c => c.status === 'Confirmado').length || 0} / {selectedMissao.numero_voluntarios} voluntários</span>
                  </div>
                </div>

                {selectedMissao.competencias_necessarias?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2">Competências</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedMissao.competencias_necessarias.map(comp => (
                        <Badge key={comp} variant="outline">{comp}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {selectedMissao.requisitos_especiais && (
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <p className="text-sm font-medium text-amber-800">Requisitos Especiais</p>
                    <p className="text-sm text-amber-700">{selectedMissao.requisitos_especiais}</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="candidaturas" className="space-y-4">
                {(!selectedMissao.candidaturas || selectedMissao.candidaturas.length === 0) ? (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p className="text-slate-500">Ainda não há candidaturas para esta missão</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selectedMissao.candidaturas.map((cand) => {
                      const candStatus = CANDIDATURA_STATUS[cand.status] || CANDIDATURA_STATUS['Pendente'];
                      return (
                        <div key={cand.id} className="p-4 border rounded-lg hover:bg-slate-50">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                                <User className="w-5 h-5 text-slate-500" />
                              </div>
                              <div>
                                <p className="font-medium">{cand.voluntario_nome || 'Voluntário'}</p>
                                <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                                  {cand.voluntario_email && (
                                    <span className="flex items-center gap-1">
                                      <Mail className="w-3 h-3" />
                                      {cand.voluntario_email}
                                    </span>
                                  )}
                                  {cand.voluntario_telefone && (
                                    <span className="flex items-center gap-1">
                                      <Phone className="w-3 h-3" />
                                      {cand.voluntario_telefone}
                                    </span>
                                  )}
                                </div>
                                {cand.voluntario_competencias?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {cand.voluntario_competencias.slice(0, 3).map(comp => (
                                      <Badge key={comp} variant="outline" className="text-xs">{comp}</Badge>
                                    ))}
                                    {cand.voluntario_competencias.length > 3 && (
                                      <Badge variant="outline" className="text-xs">+{cand.voluntario_competencias.length - 3}</Badge>
                                    )}
                                  </div>
                                )}
                                {cand.mensagem && (
                                  <p className="text-sm text-slate-600 mt-2 italic">"{cand.mensagem}"</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={`${candStatus.color} text-white`}>
                                {candStatus.label}
                              </Badge>
                              {cand.status === 'Pendente' && selectedMissao.status === 'Aberta' && (
                                <div className="flex gap-1">
                                  <Button size="sm" variant="outline" className="text-green-600 hover:bg-green-50" onClick={() => handleAceitarCandidatura(cand.id)}>
                                    <CheckCircle className="w-4 h-4" />
                                  </Button>
                                  <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleRejeitarCandidatura(cand.id)}>
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}

          <DialogFooter className="gap-2 mt-4">
            {selectedMissao?.status === 'Aberta' && (
              <Button variant="outline" className="text-red-600" onClick={() => handleCancelarMissao(selectedMissao.id)}>
                Cancelar Missão
              </Button>
            )}
            {(selectedMissao?.status === 'Aberta' || selectedMissao?.status === 'Em_Curso') && (
              <Button variant="outline" className="text-green-600" onClick={() => handleConcluirMissao(selectedMissao.id)}>
                <CheckCircle className="w-4 h-4 mr-2" />
                Marcar Concluída
              </Button>
            )}
            <Button variant="outline" onClick={() => setDetalheModalOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
